import { Grid, Paper, Typography, IconButton, Tooltip, Tabs, Tab, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { DataGrid, GridColDef, gridClasses } from '@mui/x-data-grid';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';

import { useWidgetContext } from '../../../hooks/widgetContext';
import WidgetLoading from '../../SmartDashboard/Shared/WidgetLoading';
import Authorization from '../../../utils/Authorization';
import { dateFormatter } from '../../../utils/timeFormat';
import { isDefaultDate } from '../../../utils/common';
import { parentUrl } from '../../../utils/constants';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import { fetchAndNextCensusSummary, exportAndNextCensusSummary } from '../WidgetService';
import b64toBlob from 'b64-to-blob';

const AndNextCensusSummary = () => {
    const history = useNavigate();
    const authData = new Authorization();
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [show, setShow] = useState(false);
    const [pageSize, setPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const ref = useRef(null);
    const { andNextCensusTab, setAndNextCensusTab } = useWidgetContext();

    const handleClick = (event) => {
        setShow(!show);
    };

    useEffect(() => {
        setIsFetched(false);
        const changeFilter = async () => {
            setIsFetched(false);
            const response = await fetchAndNextCensusSummary();
            setRows(response);
            setAndNextCensusTab(1);
            let filterResponse = response.filter((m) => m.Status === MemberStatusEnum.UndeterminedDescription);
            setFilteredRows(filterResponse);
            setIsFetched(true);
        };
        changeFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authData.UserId, setIsFetched]);

    const columns: GridColDef[] = [
        {
            field: 'AhcccsId',
            headerName: 'Ahcccs Id',
            flex: 1,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            flex: 1,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            flex: 1,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            flex: 1,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : dateFormatter(new Date(row.DOB))}`,
        },
        {
            field: 'HealthPlan',
            headerName: 'Health Plan',
            flex: 1,
        },
        ...(andNextCensusTab !== 1 && andNextCensusTab !== 2
            ? [
                {
                    field: 'DateOfOnboard',
                    headerName: 'Date Of Onboard',
                    flex: 1,
                    valueGetter: (value, row) => (isDefaultDate(row.DateOfOnboard) ? '-' : dateFormatter(new Date(row.DateOfOnboard))),
                },
            ]
            : []),
        ...(andNextCensusTab === 5
            ? [
                {
                    field: 'ReferralDate',
                    headerName: 'Date of Graduation',
                    flex: 1,
                    valueGetter: (value, row) => (isDefaultDate(row.ReferralDate) ? '-' : dateFormatter(new Date(row.ReferralDate))),
                },
            ]
            : []),
    ];

    const handleAlertsTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        let filterResponse = [];
        switch (newValue) {
            case 1: {
                filterResponse = rows.filter((m) => m.Status === MemberStatusEnum.UndeterminedDescription);
                break;
            }
            case 2: {
                filterResponse = rows.filter((m) => m.Status === MemberStatusEnum.ScheduledDescription);
                break;
            }
            case 3: {
                filterResponse = rows.filter((m) => m.Status === MemberStatusEnum.AndNextInProgressDescription);
                break;
            }
            case 4: {
                filterResponse = rows.filter((m) => m.Status === MemberStatusEnum.AndNextClosedDescription);
                break;
            }
            case 5: {
                filterResponse = rows.filter((m) => m.Status === MemberStatusEnum.PreparingForGraduationDescription);
                break;
            }
            case 6: {
                filterResponse = rows.filter((m) => m.Status === MemberStatusEnum.DisenrolledFromCHPDescription);
                break;
            }
        }
        setFilteredRows(filterResponse);
        setAndNextCensusTab(newValue);
    };

    const navigateToMembers = () => {
        history(`/provider/metricsummary/andnextcensusdetail`);
    };

    const getStatusCount = (status: string) => rows.filter((member) => member.Status === status).length;

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportAndNextCensusSummary();
        let blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `AndNextSummary - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
                className="smartdashboard-widget"
                elevation={5}
                sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto', position: 'relative' }}
            >
                <Button
                    style={{ minWidth: '100px', marginLeft: 'auto', float: 'right', position: 'absolute', right: '10px', top: '15px' }}
                    onClick={() => exportDetail()}
                    variant="contained"
                    color="success"
                    disabled={rows.length ? false : true}
                >
                    {downLoading ? 'Exporting...' : 'Export'}
                </Button>
                <Grid>
                    <Typography color={'primary'} variant="h6" textAlign={'center'} ref={ref}>
                        <span className="pointer" onClick={navigateToMembers}>
                            andNext Census Summary
                        </span>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="caption">Current census of members who are currently in andNext listed by status.</Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <div style={{ width: '100%', height: 'calc(100vh - 285px)', maxHeight: '500px', position: 'relative' }}>
                        {!isFetched && <WidgetLoading />}
                        <Grid size={12} pt={2}>
                            <Tabs
                                value={andNextCensusTab}
                                onChange={handleAlertsTabChange}
                                sx={{
                                    width: '100%',

                                    '& .MuiTabs-scroller': {
                                        overflow: 'visible !important',
                                    },

                                    '& .MuiTabs-flexContainer': {
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: 'repeat(1, 1fr)',
                                            sm: 'repeat(2, 1fr)',
                                            md: 'repeat(3, 1fr)',
                                        },
                                        gap: 0.5,
                                    },

                                    '& .MuiTab-root': {
                                        minWidth: 0,
                                        maxWidth: 'none',
                                        minHeight: 48,
                                        padding: '8px 10px',
                                        fontSize: '0.78rem',
                                        lineHeight: 1.3,
                                        whiteSpace: 'normal',
                                        textAlign: 'center',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                    },

                                    '& .Mui-selected': {
                                        backgroundColor: 'action.selected',
                                        fontWeight: 600,
                                    },

                                    '& .MuiTabs-indicator': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <Tab value={1} label={`Undetermined (${getStatusCount(MemberStatusEnum.UndeterminedDescription)})`} />

                                <Tab value={2} label={`Scheduled (${getStatusCount(MemberStatusEnum.ScheduledDescription)})`} />

                                <Tab value={3} label={`andNext In Progress (${getStatusCount(MemberStatusEnum.AndNextInProgressDescription)})`} />

                                <Tab value={4} label={`andNext Closed (${getStatusCount(MemberStatusEnum.AndNextClosedDescription)})`} />

                                <Tab value={5} label={`Preparing For Graduation (${getStatusCount(MemberStatusEnum.PreparingForGraduationDescription)})`} />

                                <Tab value={6} label={`Disenrolled From CHP (${getStatusCount(MemberStatusEnum.DisenrolledFromCHPDescription)})`} />
                            </Tabs>
                        </Grid>
                        <Grid size={12} p={2}>
                            <div style={{ width: '100%', height: 'calc(100vh - 350px)', maxHeight: '500px' }}>
                                <DataGrid
                                    onCellClick={(params: any) => {
                                        window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                    }}
                                    rows={filteredRows}
                                    columns={columns}
                                    paginationModel={{ pageSize: pageSize, page: page }}
                                    onPaginationModelChange={(model) => {
                                        setPageSize(model.pageSize);
                                        setPage(model.page);
                                    }}
                                    getRowId={(row) => row.Id}
                                    getRowHeight={() => 'auto'}
                                    sx={{
                                        [`& .${gridClasses.cell}`]: {
                                            py: 1,
                                        },
                                        [`& .green`]: {
                                            color: 'green',
                                        },
                                        [`& .red`]: {
                                            color: 'red',
                                        },
                                        [`& .black`]: {
                                            color: 'rgba(0,0,0,0.87)',
                                        },
                                    }}
                                />
                            </div>
                        </Grid>
                    </div>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default AndNextCensusSummary;
